"use client";

import { Suspense, useCallback, lazy, type RefObject, useContext, useEffect, useRef, useState } from "react";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useIsPhone } from "@/hooks/useIsPhone";
import { cn } from "@/lib/utils";
import { readLocalStorage, writeLocalStorage } from "@/utils/localStorage.utils";
import { useSideChatStore } from "@/stores/sideChatStore";
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore";
import { Button, Glyph, Keycap, RailActionRow, Tooltip, WilsonFace } from "@/components/desktop/ui";
import type { ChatInterfaceRef } from "@/components/chat/types/chatInterface.types";
import { FeatureRequestButton } from "./FeatureRequestButton";
import { TopBarContext } from "./TopBarContext";
import { useIsSideChatOverlay } from "./useSideChatInset";
import { WORK_LINE_ON_PAGE, parseIsListOpen } from "./chatHome.utils";
import { OVERLAY_MOTION } from "@/components/desktop/ui/overlayMotion.constants";
import { DESKTOP_Z_INDEX, ISLAND_LAYOUT, SIDE_CHAT_LAYOUT } from "./desktopUI.constants";
const ChatInterface = lazy(() => import("@/components/ChatInterface").then(mod => ({
  default: mod.ChatInterface
})));
const SIDE_CHAT_OPEN_KEY = "wilson.appSideChat.isOpen";
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PORTALLED_OVERLAY = "[data-radix-popper-content-wrapper], [role='dialog']";
const OVERLAY_SHEET = "inset-y-0 right-0 overflow-hidden rounded-l-sheet shadow-active duration-base ease-emphasized motion-safe:animate-in motion-safe:slide-in-from-right";
function useSheetKeyboard({
  sheetRef,
  isSheetShown,
  onClose
}: {
  sheetRef: RefObject<HTMLElement | null>;
  isSheetShown: boolean;
  onClose: () => void;
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!isSheetShown) return;
    const returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKeyDown = (event: KeyboardEvent) => {
      const sheet = sheetRef.current;
      if (!sheet || !(event.target instanceof Node)) return;
      const isInSheet = sheet.contains(event.target);
      if (event.key === "Escape" && isInSheet) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const isInOwnOverlay = event.target instanceof Element && !!event.target.closest(PORTALLED_OVERLAY);
      if (!isInSheet && isInOwnOverlay) return;
      const focusable = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const isLeavingStart = event.shiftKey && (event.target === first || !isInSheet);
      const isLeavingEnd = !event.shiftKey && (event.target === last || !isInSheet);
      if (isLeavingStart) {
        event.preventDefault();
        last.focus();
      } else if (isLeavingEnd) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      returnFocusTo?.focus();
    };
  }, [isSheetShown, sheetRef]);
}
function useStoredIsOpen() {
  const isOpen = useSideChatStore(state => state.isOpen);
  const setIsOpen = useSideChatStore(state => state.setIsOpen);
  useEffect(() => {
    const stored = readLocalStorage({
      key: SIDE_CHAT_OPEN_KEY,
      parse: parseIsListOpen
    });
    if (stored !== null) setIsOpen(stored);
  }, [setIsOpen]);
  useEffect(() => {
    writeLocalStorage({
      key: SIDE_CHAT_OPEN_KEY,
      value: String(isOpen)
    });
  }, [isOpen]);
  return [isOpen, setIsOpen] as const;
}
export function AppSideChat({
  isShown
}: {
  isShown: boolean;
}) {
  const t = useClientTranslations("desktop.chatHome");
  const subject = useContext(TopBarContext)?.wilsonSubject ?? "";
  const [isOpen, setIsOpen] = useStoredIsOpen();
  const focusRequest = useSideChatStore(state => state.focusRequest);
  const setRecordSlot = useSideChatStore(state => state.setRecordSlot);
  const isRecordInFront = useSideChatStore(state => state.recordClaims > 0);
  const chatRef = useRef<ChatInterfaceRef>(null);
  const stagedWindowId = useDesktopStore(state => getStagedWindow(state.windows)?.id);
  const stagedWindowData = useDesktopStore(state => getStagedWindow(state.windows)?.data);
  useEffect(() => {
    if (stagedWindowId) chatRef.current?.startNewConversation();
  }, [stagedWindowId]);
  const [hasBeenShown, setHasBeenShown] = useState(isShown);
  if (isShown && !hasBeenShown) setHasBeenShown(true);
  const isPhone = useIsPhone();
  const isOverlay = useIsSideChatOverlay();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  if (!isShown && isSheetOpen) setIsSheetOpen(false);
  useEffect(() => {
    setIsSheetOpen(false);
  }, [stagedWindowId, stagedWindowData]);
  const sheetRef = useRef<HTMLElement>(null);
  useSheetKeyboard({
    sheetRef,
    isSheetShown: isOverlay && isShown && isSheetOpen,
    onClose: () => setIsSheetOpen(false)
  });
  const isPanelOpen = isOverlay ? isSheetOpen : isOpen;
  const closePanel = () => isOverlay ? setIsSheetOpen(false) : setIsOpen(false);
  const setAskButtonWidth = useSideChatStore(state => state.setAskButtonWidth);
  const askButtonObserver = useRef<ResizeObserver | null>(null);
  const reportAskButtonWidth = useCallback((el: HTMLDivElement | null) => {
    askButtonObserver.current?.disconnect();
    askButtonObserver.current = null;
    if (!el) {
      setAskButtonWidth(0);
      return;
    }
    setAskButtonWidth(el.offsetWidth);
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(() => setAskButtonWidth(el.offsetWidth));
    observer.observe(el);
    askButtonObserver.current = observer;
  }, [setAskButtonWidth]);
  const handledFocusRequest = useRef(0);
  useEffect(() => {
    if (focusRequest === handledFocusRequest.current) return;
    handledFocusRequest.current = focusRequest;
    if (isOverlay) setIsSheetOpen(true);else setIsOpen(true);
    const frame = requestAnimationFrame(() => {
      const {
        recordClaims,
        focusRecordChat
      } = useSideChatStore.getState();
      if (recordClaims > 0 && focusRecordChat) focusRecordChat();else chatRef.current?.focusInput();
    });
    return () => cancelAnimationFrame(frame);
  }, [focusRequest, isOverlay, setIsOpen]);
  const pendingAsk = useSideChatStore(state => state.pendingAsk);
  const clearPendingAsk = useSideChatStore(state => state.clearPendingAsk);
  if (!hasBeenShown) return null;
  return <>
            {isOverlay && isShown && isSheetOpen && <div aria-hidden data-state="open" onClick={closePanel} className={cn("fixed inset-0 bg-scrim-soft", OVERLAY_MOTION.scrim)} style={{
      zIndex: DESKTOP_Z_INDEX.SPOTLIGHT
    }} />}
            <aside ref={sheetRef} aria-label={t("wilson")} role={isOverlay ? "dialog" : undefined} aria-modal={isOverlay ? true : undefined} className={cn("fixed flex flex-col bg-offwhite", isOverlay ? cn(OVERLAY_SHEET, isPhone && "left-6") : "inset-y-0 right-0 border-l border-ink/[0.07]", (!isShown || !isPanelOpen) && "hidden")} style={{
      width: isPhone ? undefined : SIDE_CHAT_LAYOUT.WIDTH,
      paddingTop: isOverlay ? "env(safe-area-inset-top, 0px)" : undefined,
      zIndex: isOverlay ? DESKTOP_Z_INDEX.SPOTLIGHT : DESKTOP_Z_INDEX.SIDE_CHAT
    }}>
                <div className="flex h-14 flex-shrink-0 items-center gap-2.5 px-4">
                    <WilsonFace variant="bare" className="h-5 w-5 text-ink" data-sentry-element="WilsonFace" data-sentry-source-file="AppSideChat.tsx" />
                    <span className="flex-1 text-headline font-medium text-ink">
                        {t("wilson")}
                    </span>
                    <FeatureRequestButton data-sentry-element="FeatureRequestButton" data-sentry-source-file="AppSideChat.tsx" />
                    <Tooltip side="bottom" content={t("sideChat.hide")} data-sentry-element="Tooltip" data-sentry-source-file="AppSideChat.tsx">
                        <Button variant="ghost" size="icon" onClick={closePanel} aria-label={t("sideChat.hide")} data-sentry-element="Button" data-sentry-source-file="AppSideChat.tsx">
                            <Glyph name={isOverlay ? "close" : "sidebar"} data-sentry-element="Glyph" data-sentry-source-file="AppSideChat.tsx" />
                        </Button>
                    </Tooltip>
                </div>
                {!isRecordInFront && <div className="flex flex-shrink-0 flex-col px-0.5">
                        <RailActionRow glyph="plus" label={t("sideChat.newChat")} onClick={() => chatRef.current?.startNewConversation()} />
                    </div>}
                <div ref={setRecordSlot} className={cn("min-h-0 flex-1", !isRecordInFront && "hidden")} />
                <div className={cn("min-h-0 flex-1", isRecordInFront && "hidden")}>
                    <Suspense fallback={null} data-sentry-element="Suspense" data-sentry-source-file="AppSideChat.tsx">
                        <ChatInterface ref={chatRef} shouldStartInDraft isMobile={isPhone} suggestedPrompts={[]} isCompact emptyHero={<p className="text-center text-title font-normal text-ink">
                                    {subject ? t("sideChat.askAbout", {
              subject
            }) : t("sideChat.askAnything")}
                                </p>} mainPaneClassName={WORK_LINE_ON_PAGE} autoSendMessage={isRecordInFront ? null : pendingAsk} onAutoSendDispatched={clearPendingAsk} isComposerRaised data-sentry-element="ChatInterface" data-sentry-source-file="AppSideChat.tsx" />
                    </Suspense>
                </div>
            </aside>
            {isShown && !isPanelOpen && <div ref={reportAskButtonWidth} className="fixed right-4" style={{
      top: ISLAND_LAYOUT.TOP,
      zIndex: DESKTOP_Z_INDEX.SIDE_CHAT
    }}>
                    <Button variant="secondary" onClick={() => useSideChatStore.getState().openAndFocus()} aria-keyshortcuts={isPhone ? undefined : "Meta+J"}>
                        <WilsonFace variant="bare" className="h-4 w-4" />
                        {t("sideChat.show")}
                        {!isPhone && <Keycap>⌘J</Keycap>}
                    </Button>
                </div>}
        </>;
}